import React, { useEffect, useRef } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";
import Image from "next/image";

interface Props {
  alt: string;
  height?: string | number;
  src: string;
  width?: string | number;
  className?: string;
  layout?: "fixed" | "fill" | "intrinsic" | "responsive";
  objectFit?: "fill" | "contain" | "cover" | "none" | "scale-down";
  onClick?: () => void;
  onError?: () => void;
  priority?: boolean;
  isLazyLoad?: boolean;
  srcSet?: string;
  sizes?: string;
}

const CustomImage: React.FC<Props> = ({
  src,
  width,
  height,
  alt,
  className,
  layout = "intrinsic",
  objectFit,
  onClick,
  onError,
  priority = false,
  isLazyLoad = true,
  srcSet,
  sizes

}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!priority || !wrapperRef.current) return;

    const tryInject = () => {
      const img =
        wrapperRef.current?.querySelector("img") ||
        wrapperRef.current?.querySelector("span img") ||
        wrapperRef.current?.querySelector("span span img");

      if (img) {
        img.setAttribute("fetchpriority", "high");
      } else {
        // Retry after delay if img not found yet (hydration delay)
        setTimeout(tryInject, 50);
      }
    };

    tryInject();
  }, [priority]);

  const image = (
    <Image
      src={src}
      alt={alt}
      layout={layout}
      width={layout !== "fill" ? width : undefined}
      height={layout !== "fill" ? height : undefined}
      objectFit={objectFit}
      onClick={onClick}
      onError={onError}
      priority={priority}
      loading={isLazyLoad ? "lazy" : "eager"}
      className={className}
      unoptimized
      fetchpriority="high"
      sizes={srcSet}
      // placeholder="blur"
    />
  );

  // Only wrap in div if `priority` is true
  if (priority) {
    return (
      <div
        ref={wrapperRef}
        style={
          layout === "fill"
            ? { position: "relative", width: "100%", height: "100%" }
            : undefined
        }
      >
        {image}
      </div>
    );
  }

  return <LazyLoadComponent>{image}</LazyLoadComponent>;
};

export default CustomImage;
